import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { ErrorState, Notice, PageHeader, RouteTabs } from "../../../../components/ui";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import { financeAPI } from "../../../services/businessErpService";
import { HISTORY_PATH, PAYMENTS_PATH, PAYROLL_BASE, openSalaryCount, periodStatus } from "./payrollFinance";

/**
 * Finance › Payroll (UI-2). One workspace, three real routes:
 *   To review  — payroll HR submitted for a Finance decision
 *   To pay     — released salaries still to hand over or be confirmed
 *   History    — everything Finance has no action on
 * The server stays the authority on every action; the permission checks
 * here only decide which controls are offered.
 */
export default function FinancePayrollLayout() {
  const auth = useBusinessAuth();
  const isOwner = String(auth?.role || "").toUpperCase() === "OWNER";
  // Approving payroll and recording payments are separate permissions (FIN-1).
  const canApprove = isOwner || Boolean(auth?.hasPermission?.("finance:approve"));
  const canRecordPayments = isOwner || Boolean(auth?.hasPermission?.("finance:payments:record"));

  const [periods, setPeriods] = useState([]);
  const [closed, setClosed] = useState({ page: 1, pageSize: 25, total: 0 });
  const [closedPage, setClosedPage] = useState(1);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [notice, setNotice] = useState(null);
  const { pathname } = useLocation();

  const reload = useCallback(async () => {
    try {
      const res = await financeAPI.getPayroll(closedPage > 1 ? { closedPage } : {});
      setPeriods(Array.isArray(res?.data?.periods) ? res.data.periods : []);
      if (res?.data?.closed) setClosed(res.data.closed);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [closedPage]);

  useEffect(() => {
    reload();
  }, [reload]);

  // A confirmation belongs to the lane where the action happened.
  useEffect(() => {
    setNotice(null);
  }, [pathname]);

  const counts = useMemo(() => ({
    review: periods.filter((row) => periodStatus(row) === "under_review").length,
    pay: periods
      .filter((row) => periodStatus(row) === "released")
      .reduce((sum, row) => sum + openSalaryCount(row), 0),
  }), [periods]);

  const context = useMemo(
    () => ({ periods, loading: state === "loading", reload, setNotice, canApprove, canRecordPayments, isOwner, closed, setClosedPage }),
    [periods, state, reload, canApprove, canRecordPayments, isOwner, closed]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll"
        description="Decide on the payroll HR sends you, then pay each salary once HR releases the payslips."
        navigation={
          <RouteTabs
            label="Payroll"
            items={[
              { to: PAYROLL_BASE, label: "To review", count: counts.review, end: true },
              { to: PAYMENTS_PATH, label: "To pay", count: counts.pay },
              { to: HISTORY_PATH, label: "History" },
              { to: `${PAYROLL_BASE}/corrections`, label: "Corrections" },
            ]}
          />
        }
      />

      {notice ? (
        <Notice tone={notice.tone || "success"} role="status">
          {notice.text}
        </Notice>
      ) : null}

      {state === "error" ? (
        <ErrorState
          title="Payroll couldn't be loaded"
          description="Check your connection and try again."
          onRetry={() => {
            setState("loading");
            reload();
          }}
        />
      ) : (
        <Outlet context={context} />
      )}
    </div>
  );
}
